import React, { useState, useEffect, useMemo } from "react";
import { LICENSE_KIND_LABELS, LicenseKind } from "../../types/licenseTypes";
import {
  License,
  EnrichedLicense,
  LicenseSeat,
  CreateLicenseData,
  EditLicenseData,
  User,
  Company,
  Project,
  ExtendedUser,
} from "../../store/types";
import { useGetQuery } from "../../store/slices/apiSlice";
import { validateLicenseField } from "../../hooks/validation";
import {
  getLicenseKind,
  getLicenseKindLabel,
} from "../../hooks/useLicenseType";
import { formatDate } from "../../hooks/dateTimeConvertion";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import CustomInputNumberIncrement from "../CustomInput/CustomInputNumberIncrement";
import CustomDropdown from "../CustomInput/CustomDropdown";
import CustomCalendar from "../CustomInput/CustomCalendar";
import { StyledIconLine } from "../StyledIcon/StyledIcon";
import { BodyText, DangerText } from "../../styles/Typography";
import {
  IconPlusBlack,
  IconCalendar,
  IconDocument,
  IconCompany,
  IconCalendarCheckFilled,
  IconCalendarXFilled,
  IconDocFilled,
  IconCompanyFilled,
  IconCaseFilled,
  IconCase,
  IconUserFilled,
  IconUser,
} from "../../assets/icons/Icon";
import { Column, SmallColumn, Row } from "./Dialogs.styles";

interface DialogProps {
  visible: boolean;
  onHide: () => void;
  dialogType: string;
  user?: User | ExtendedUser | null;
  company?: Company;
  project?: Project;
  license?: License | null;
  enrichedLicense?: EnrichedLicense | null;
  handleActivateLicense?: (id: string) => Promise<boolean>;
  handleDeactivateLicense?: (id: string) => Promise<boolean>;
  handleAddLicense?: (
    license: CreateLicenseData
  ) => Promise<{ success: boolean; licenseIds?: string[] }>;
  handleEditLicense?: (
    license: EditLicenseData,
    id: string
  ) => Promise<boolean>;
  /** PUT /api/v2/licenses/{licenseId}/seats/{userId} */
  handleAssignSeat?: (licenseId: string, userId: string) => Promise<boolean>;
  /** DELETE /api/v2/licenses/{licenseId}/seats/{userId} */
  handleRemoveSeat?: (licenseId: string, userId: string) => Promise<boolean>;
  handleDeleteLicense?: (license: License) => Promise<boolean>;
  refetchData?: () => {};
  refetchLicenseUser?: () => {};
  usersList?: User[] | null;
  usersOptions?: { label: string; value: any }[];
  companiesOptions?: { label: string; value: any }[];
  projectsOptions?: { label: string; value: any }[];
  licensesEP: string;
}

const initialFormData: CreateLicenseData = {
  kind: null,
  projectId: "",
  organizationId: 0,
  startsAt: null,
  expiresAt: null,
  maxActiveUsers: null,
  comments: "",
  count: 1,
};

const initialErrors: Record<keyof CreateLicenseData, boolean> = {
  kind: false,
  projectId: false,
  organizationId: false,
  startsAt: false,
  expiresAt: false,
  maxActiveUsers: false,
  comments: false,
  count: false,
};

const seatName = (seat?: LicenseSeat | null): string =>
  seat?.fullName || seat?.shortName || "Не добавлен";

const LicenseDialogs: React.FC<DialogProps> = ({
  visible,
  onHide,
  dialogType,
  user,
  company,
  project,
  license,
  enrichedLicense,
  handleActivateLicense,
  handleDeactivateLicense,
  handleAddLicense,
  handleEditLicense,
  handleAssignSeat,
  handleRemoveSeat,
  handleDeleteLicense,
  refetchData,
  refetchLicenseUser,
  usersList = [],
  usersOptions = [],
  companiesOptions = [],
  projectsOptions = [],
  licensesEP,
}) => {
  const [localLicense, setLocalLicense] = useState<License | null>(null);
  const [formData, setFormData] = useState<CreateLicenseData>(initialFormData);
  const [errors, setErrors] =
    useState<Record<keyof CreateLicenseData, boolean>>(initialErrors);
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [addUserOpt, setAddUserOpt] = useState<{ label: string; value: any }[]>(
    []
  );
  const [isAvailableUsers, setIsAvailableUsers] = useState<boolean>(true);
  const [isAddUser, setIsAddUser] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");

  // Карточка лицензии v2 нужна ради seats: id пользователей мест приходят только здесь
  const { data: fetchedLicense } = useGetQuery(`${licensesEP}/${license?.id}`, {
    refetchOnMountOrArgChange: true,
    skip:
      !visible ||
      !license?.id ||
      (dialogType !== "edit" &&
        dialogType !== "addUser" &&
        dialogType !== "editUser" &&
        dialogType !== "deleteUser"),
  });

  const seats: LicenseSeat[] = useMemo(
    () => fetchedLicense?.seats ?? localLicense?.seats ?? [],
    [fetchedLicense, localLicense]
  );
  const currentSeat = seats.length > 0 ? seats[0] : null;

  const isPerpetual = formData.kind === LicenseKind.Perpetual;
  const isSession = formData.kind === LicenseKind.Session;

  const handleFieldChange = (field: keyof CreateLicenseData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    if (dialogType !== "create" && dialogType !== "edit") return true;

    const fields: (keyof CreateLicenseData)[] =
      dialogType === "create"
        ? [
            "count",
            "kind",
            "projectId",
            "organizationId",
            "startsAt",
            "expiresAt",
            "maxActiveUsers",
          ]
        : ["startsAt", "expiresAt"];

    const newErrors = { ...initialErrors };
    fields.forEach((field) => {
      newErrors[field] = !validateLicenseField(
        field,
        formData[field],
        formData
      );
    });

    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleBlur = (field: keyof CreateLicenseData) => {
    const isValid = validateLicenseField(field, formData[field], formData);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (!validateAllFields()) {
      setValidForm(false);
      return;
    }

    setValidForm(true);
    setSubmitting(true);
    let success = false;

    try {
      if (dialogType === "edit" && isAddUser) {
        if (!selectedUser) {
          setValidForm(false);
          return;
        }
        success = await handleAssignSeat!(license!.id, selectedUser);
      } else {
        switch (dialogType) {
          case "create": {
            const addResult = await handleAddLicense!(formData);
            success = addResult.success;
            // при создании из карточки пользователя сразу занимаем место
            if (success && user && addResult.licenseIds) {
              for (const licenseId of addResult.licenseIds) {
                await handleAssignSeat!(licenseId, user.id);
              }
            }
            break;
          }
          case "edit": {
            const editData: EditLicenseData = {
              startsAt: formData.startsAt,
              expiresAt: isPerpetual ? null : formData.expiresAt,
              comments: comment,
            };
            success = await handleEditLicense!(editData, license!.id);
            break;
          }
          case "addUser": {
            if (!selectedUser) return;
            success = await handleAssignSeat!(license!.id, selectedUser);
            break;
          }
          case "editUser": {
            if (!selectedUser) return;
            if (currentSeat && currentSeat.userId === selectedUser) {
              success = true;
              break;
            }
            // место одно: сначала освобождаем прежнее, потом занимаем новым пользователем
            if (currentSeat) {
              success = await handleRemoveSeat!(
                license!.id,
                currentSeat.userId
              );
              if (!success) break;
            }
            success = await handleAssignSeat!(license!.id, selectedUser);
            break;
          }
          case "deleteUser": {
            if (!currentSeat) {
              success = false;
              break;
            }
            success = await handleRemoveSeat!(license!.id, currentSeat.userId);
            break;
          }
          case "delete":
            success = await handleDeleteLicense!(license!);
            break;
          case "enable":
            success = await handleActivateLicense!(license!.id);
            break;
          case "disable":
            success = await handleDeactivateLicense!(license!.id);
            break;
          default:
            success = false;
        }
      }

      if (success) {
        refetchData!();
        refetchLicenseUser?.();
        setLocalLicense(null);
        onHide();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleHide = () => {
    setLocalLicense(null);
    onHide();
  };

  useEffect(() => {
    if (fetchedLicense) {
      setLocalLicense(fetchedLicense);
    }
  }, [fetchedLicense, dialogType, visible]);

  useEffect(() => {
    if (!visible) return;

    setValidForm(true);
    if (dialogType === "edit" && localLicense) {
      setFormData({
        kind: getLicenseKind(localLicense.kind),
        projectId: localLicense.projectId || "",
        organizationId: localLicense.organizationId || 0,
        startsAt: localLicense.startsAt ? new Date(localLicense.startsAt) : null,
        expiresAt: localLicense.expiresAt
          ? new Date(localLicense.expiresAt)
          : null,
        maxActiveUsers: localLicense.maxActiveUsers ?? null,
        comments: localLicense.comments || "",
        count: 1,
      });
      setErrors(initialErrors);
      setComment(localLicense.comments || "");
    } else if (dialogType === "create") {
      setFormData({
        ...initialFormData,
        projectId: project?.id ?? "",
        organizationId: company?.id ?? 0,
      });
      setErrors(initialErrors);
      setComment("");
    } else {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setComment("");
    }
  }, [visible, dialogType, localLicense, project, company]);

  useEffect(() => {
    if (!visible) {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setValidForm(true);
      setSubmitting(false);
      setLocalLicense(null);
      setComment("");
      setAddUserOpt([]);
      setSelectedUser("");
      setIsAddUser(false);
    }
  }, [visible]);

  // Бессрочная лицензия не имеет даты окончания
  useEffect(() => {
    if (isPerpetual && formData.expiresAt) {
      setFormData((prev) => ({ ...prev, expiresAt: null }));
      setErrors((prev) => ({ ...prev, expiresAt: false }));
    }
  }, [isPerpetual, formData.expiresAt]);

  // Сессионная лицензия создаётся всегда в одном экземпляре
  useEffect(() => {
    if (isSession && formData.count !== 1) {
      setFormData((prev) => ({ ...prev, count: 1 }));
      setErrors((prev) => ({ ...prev, count: false }));
    }
  }, [isSession, formData.count]);

  useEffect(() => {
    if (
      (dialogType === "addUser" || dialogType === "editUser" || isAddUser) &&
      license?.organizationId &&
      usersList
    ) {
      const filtered = usersList
        .filter((u) => u.organizationId === license.organizationId)
        .map((u) => ({
          label: `${u.lastName ?? ""} ${u.name ?? ""} ${u.patronymic ?? ""}`,
          value: u.id,
        }));
      setAddUserOpt(filtered.length > 0 ? filtered : usersOptions);
      if (dialogType === "editUser") {
        if (currentSeat) setSelectedUser(currentSeat.userId);
        else if (user) setSelectedUser(user.id);
      }
    } else {
      setAddUserOpt([]);
      setSelectedUser("");
    }
  }, [dialogType, isAddUser, license, currentSeat, user, usersList]);

  useEffect(() => {
    setIsAvailableUsers(addUserOpt.length > 0);
  }, [addUserOpt]);

  const getHeader = (type: string) => {
    switch (type) {
      case "create":
        return "Новая лицензия";
      case "edit":
        return "Редактировать лицензию";
      case "delete":
        return "Удалить лицензию";
      case "enable":
        return "Активировать лицензию";
      case "disable":
        return "Деактивировать лицензию";
      case "addUser":
        return "Добавить пользователя";
      case "editUser":
        return "Редактировать пользователя";
      case "deleteUser":
        return "Удалить пользователя";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const getButtonLabel = (type: string) => {
    switch (type) {
      case "create":
        return "Создать";
      case "edit":
        return "Сохранить";
      case "delete":
        return "Удалить";
      case "enable":
        return "Активировать";
      case "disable":
        return "Деактивировать";
      case "addUser":
        return "Добавить";
      case "editUser":
        return "Сохранить";
      case "deleteUser":
        return "Удалить";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const footerContent = (
    <div>
      <Button label="Отмена" onClick={handleHide} outlined />
      <Button
        label={getButtonLabel(dialogType)}
        onClick={handleSubmit}
        loading={submitting}
        severity={dialogType === "disable" ? "danger" : undefined}
      />
    </div>
  );

  const usersDropdownForm = (
    <>
      {isAvailableUsers ? (
        <CustomDropdown
          value={selectedUser}
          onChange={(val) => setSelectedUser(val)}
          options={addUserOpt}
          placeholder="Пользователь"
          icon={<IconUser size={20} />}
          filter
        />
      ) : (
        <DangerText>В этой компании нет пользователей</DangerText>
      )}
    </>
  );

  const kindLabel =
    enrichedLicense?.kindLabel ?? getLicenseKindLabel(license?.kind);

  const formContent = (
    <Column>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      {dialogType === "create" ? (
        <>
          {!isSession && (
            <CustomInputNumberIncrement
              value={formData.count}
              setValue={(val) => handleFieldChange("count", val)}
              onBlur={() => handleBlur("count")}
              placeholder="Количество лицензий"
              invalid={errors.count}
            />
          )}
          <CustomDropdown
            value={formData.kind}
            onChange={(val) => handleFieldChange("kind", val)}
            onBlur={() => handleBlur("kind")}
            options={LICENSE_KIND_LABELS}
            placeholder="Тип лицензии"
            icon={<IconDocument size={20} />}
            invalid={errors.kind}
          />
          {project ? (
            <></>
          ) : (
            <CustomDropdown
              value={formData.projectId}
              onChange={(val) => handleFieldChange("projectId", val)}
              onBlur={() => handleBlur("projectId")}
              options={projectsOptions}
              placeholder="Проект"
              icon={<IconCase size={20} />}
              invalid={errors.projectId}
              filter
            />
          )}
          {company ? (
            <></>
          ) : (
            <CustomDropdown
              value={formData.organizationId}
              onChange={(val) => handleFieldChange("organizationId", val)}
              onBlur={() => handleBlur("organizationId")}
              options={companiesOptions}
              placeholder="Компания"
              icon={<IconCompany size={20} />}
              invalid={errors.organizationId}
              filter
            />
          )}
          {isSession && (
            <CustomInputNumberIncrement
              value={formData.maxActiveUsers}
              setValue={(val) => handleFieldChange("maxActiveUsers", val)}
              onBlur={() => handleBlur("maxActiveUsers")}
              placeholder="Максимум активных пользователей"
              invalid={errors.maxActiveUsers}
            />
          )}
          <Row>
            <CustomCalendar
              key="start-date"
              value={formData.startsAt}
              setValue={(date) => handleFieldChange("startsAt", date)}
              onBlur={() => handleBlur("startsAt")}
              placeholder="Дата начала"
              icon={<IconCalendar size={20} />}
              invalid={errors.startsAt}
            />
            {!isPerpetual && (
              <>
                <BodyText>—</BodyText>
                <CustomCalendar
                  key="end-date"
                  value={formData.expiresAt}
                  setValue={(date) => handleFieldChange("expiresAt", date)}
                  onBlur={() => handleBlur("expiresAt")}
                  placeholder="Дата окончания"
                  icon={<IconCalendar size={20} />}
                  invalid={errors.expiresAt}
                />
              </>
            )}
          </Row>
          {isPerpetual && <BodyText>Лицензия бессрочная</BodyText>}
          <InputTextarea
            autoResize
            value={formData.comments}
            onChange={(e) => handleFieldChange("comments", e.target.value)}
            rows={3}
            cols={30}
            placeholder="Комментарий"
          />
        </>
      ) : localLicense ? (
        <>
          <SmallColumn>
            <StyledIconLine
              icon={<IconCaseFilled size={20} />}
              text={localLicense.projectName ?? "Нет данных"}
            />
            <StyledIconLine
              icon={<IconCompanyFilled size={20} />}
              text={`${localLicense.organizationName ?? "Нет данных"}`}
            />
            {currentSeat && (
              <StyledIconLine
                icon={<IconUserFilled size={20} />}
                text={seatName(currentSeat)}
              />
            )}
          </SmallColumn>
          <Row>
            <CustomCalendar
              value={formData.startsAt}
              setValue={(date) => handleFieldChange("startsAt", date)}
              onBlur={() => handleBlur("startsAt")}
              placeholder="Дата начала"
              icon={<IconCalendar size={20} />}
              invalid={errors.startsAt}
            />
            {!isPerpetual ? (
              <>
                <BodyText>—</BodyText>
                <CustomCalendar
                  value={formData.expiresAt}
                  setValue={(date) => handleFieldChange("expiresAt", date)}
                  onBlur={() => handleBlur("expiresAt")}
                  placeholder="Дата окончания"
                  icon={<IconCalendar size={20} />}
                  invalid={errors.expiresAt}
                />
              </>
            ) : (
              <BodyText>Бессрочная</BodyText>
            )}
          </Row>
          <InputTextarea
            autoResize
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={5}
            cols={30}
            placeholder="Комментарий"
          />
          {currentSeat ? (
            <></>
          ) : (
            <>
              <Button
                outlined
                severity="secondary"
                onClick={() => setIsAddUser(true)}
                visible={!isAddUser}
              >
                <IconPlusBlack size={20} />
                <BodyText style={{ marginLeft: "5px" }}>
                  Добавить пользователя
                </BodyText>
              </Button>
              {isAddUser ? usersDropdownForm : <></>}
            </>
          )}
        </>
      ) : (
        <BodyText>Загрузка...</BodyText>
      )}
    </Column>
  );

  const addUserForm = (
    <Column>
      <StyledIconLine
        icon={<IconCaseFilled size={20} />}
        text={enrichedLicense?.projectName ?? license?.projectName ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={`${
          enrichedLicense?.organizationName ??
          license?.organizationName ??
          "Нет данных"
        }`}
      />
      <StyledIconLine
        icon={<IconDocFilled size={20} />}
        text={kindLabel ? `${kindLabel} лицензия` : "Нет данных"}
      />
      {usersDropdownForm}
    </Column>
  );

  const infoForm = (
    <SmallColumn>
      <StyledIconLine
        icon={<IconCaseFilled size={20} />}
        text={enrichedLicense?.projectName ?? license?.projectName ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={`${
          enrichedLicense?.organizationName ??
          license?.organizationName ??
          "Нет данных"
        }`}
      />
      {(dialogType === "deleteUser" || currentSeat) && (
        <StyledIconLine
          icon={<IconUserFilled size={20} />}
          text={seatName(currentSeat)}
        />
      )}
      <StyledIconLine
        icon={<IconDocFilled size={20} />}
        text={kindLabel ? `${kindLabel} лицензия` : "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCalendarCheckFilled size={20} />}
        text={`Дата начала: ${
          license?.startsAt ? formatDate(license.startsAt.toString()) : "Нет данных"
        }`}
      />
      <StyledIconLine
        icon={<IconCalendarXFilled size={20} />}
        text={`Дата завершения: ${
          license?.expiresAt
            ? formatDate(license.expiresAt.toString())
            : "Бессрочно"
        }`}
      />
    </SmallColumn>
  );

  const deleteForm = (
    <Column>
      <DangerText>Вы уверены, что хотите удалить данную лицензию?</DangerText>
      {infoForm}
    </Column>
  );

  const deleteUserForm = (
    <Column>
      <DangerText>
        Вы уверены, что хотите удалить пользователя из лицензии?
      </DangerText>
      {infoForm}
    </Column>
  );

  const enableForm = (
    <Column>
      <BodyText>Активировать данную лицензию?</BodyText>
      {infoForm}
    </Column>
  );

  const disableForm = (
    <Column>
      <DangerText>
        Вы уверены, что хотите деактивировать данную лицензию?
      </DangerText>
      {infoForm}
    </Column>
  );

  const getContent = (type: string) => {
    switch (type) {
      case "create":
        return formContent;
      case "edit":
        return formContent;
      case "delete":
        return deleteForm;
      case "enable":
        return enableForm;
      case "disable":
        return disableForm;
      case "addUser":
        return addUserForm;
      case "editUser":
        return addUserForm;
      case "deleteUser":
        return deleteUserForm;
      default:
        return <></>;
    }
  };

  return (
    <Dialog
      header={getHeader(dialogType)}
      visible={visible}
      style={{ width: "500px" }}
      onHide={handleHide}
      footer={footerContent}
    >
      {getContent(dialogType)}
    </Dialog>
  );
};

export default LicenseDialogs;
